/**
 * AI Assistant Chat
 *
 * Displays the current conversation with messages.
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Typography, alpha, CircularProgress } from '@mui/material';
import { useRootStore } from '../../store/contexts';
import { tokenManager } from '../../api/tokenManager';
import { AiAssistantMessage } from './AiAssistantMessage';
import { QueryExplanationPanel } from './QueryExplanationPanel';
import { EvaluationForm } from './EvaluationForm';
import { useAiSearchStream, StreamEvent, StreamState, ToolPayload, DeltaPayload, ExplanationPayload } from './useAiSearchStream';

interface WelcomeMessageProps {
  onSuggestionClick: (query: string) => void;
}

const WelcomeMessage: React.FC<WelcomeMessageProps> = ({ onSuggestionClick }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      textAlign: 'center',
      p: 3,
    }}
  >
    <Box
      component="img"
      src="/IO.png"
      alt="iO"
      sx={{
        width: 80,
        height: 80,
        objectFit: 'contain',
        mb: 2,
        opacity: 0.9,
      }}
    />
    <Typography variant="h6" gutterBottom>
      iO — Motor de consultas
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280 }}>
      Consulta datos: obras, autores, editoriales, estadísticas y más.
    </Typography>
    <Box sx={{ mt: 3 }}>
      <Typography variant="caption" color="text.secondary">
        ¿Qué querés consultar?
      </Typography>
      <Box
        sx={{
          mt: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        {[
          '¿Cuántas obras hay?',
          '¿Cómo funciona iO?',
          '¿Qué datos puedo consultar?',
        ].map((suggestion) => (
          <Typography
            key={suggestion}
            variant="caption"
            onClick={() => onSuggestionClick(suggestion)}
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.16),
              },
            }}
          >
            {suggestion}
          </Typography>
        ))}
      </Box>
    </Box>
  </Box>
);

interface StreamingIndicatorProps {
  toolStatus: { name: string; status: 'running' | 'done'; count?: number; elapsedMs?: number } | null;
  content: string;
}

// Format elapsed time for display
const formatElapsedTime = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

const StreamingIndicator: React.FC<StreamingIndicatorProps> = ({ toolStatus, content }) => {
  // Tool names to Spanish
  const toolLabels: Record<string, string> = {
    query_entities: 'Consultando datos',
    count_entities: 'Contando registros',
    aggregate_entities: 'Agregando datos',
    search_documents: 'Buscando documentos',
    next_page: 'Cargando más resultados',
    get_lookup_values: 'Obteniendo valores',
  };

  // Show tool status if active
  if (toolStatus) {
    const label = toolLabels[toolStatus.name] || toolStatus.name;
    const elapsedText = toolStatus.elapsedMs ? ` (${formatElapsedTime(toolStatus.elapsedMs)})` : '';
    const statusText = toolStatus.status === 'running'
      ? `${label}...${elapsedText}`
      : toolStatus.count !== undefined
        ? `${label}: ${toolStatus.count} encontrados${elapsedText}`
        : `${label} completado${elapsedText}`;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
        }}
      >
        {toolStatus.status === 'running' && <CircularProgress size={16} />}
        <Typography variant="body2" color="text.secondary">
          {statusText}
        </Typography>
      </Box>
    );
  }

  // Show "thinking" if no content yet
  if (!content) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
        }}
      >
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Pensando...
        </Typography>
      </Box>
    );
  }

  return null;
};

const LoadingIndicator: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 2,
      py: 1.5,
    }}
  >
    <CircularProgress size={16} />
    <Typography variant="body2" color="text.secondary">
      Pensando...
    </Typography>
  </Box>
);

export const AiAssistantChat: React.FC = observer(() => {
  const { aiAssistantStore } = useRootStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Handle streaming events (same as AiAssistantInput)
  const handleStreamEvent = useCallback((event: StreamEvent) => {
    switch (event.type) {
      case 'tool':
        aiAssistantStore.updateToolStatus(event.payload as ToolPayload);
        break;
      case 'delta':
        aiAssistantStore.updateStreamingContent((event.payload as DeltaPayload).content);
        break;
      case 'explanation':
        aiAssistantStore.setExplanation(event.payload as ExplanationPayload);
        break;
      case 'done':
        // Handled by onComplete callback for full state access
        break;
      case 'error':
        aiAssistantStore.handleStreamingError((event.payload as { message: string }).message);
        break;
    }
  }, [aiAssistantStore]);

  // Handle stream completion with full state (including responseId)
  const handleStreamComplete = useCallback((state: StreamState) => {
    aiAssistantStore.finishStreaming(
      state.conversationId || '',
      state.sources,
      state.responseId
    );
  }, [aiAssistantStore]);

  const { sendMessage: sendStreamMessage } = useAiSearchStream({
    onEvent: handleStreamEvent,
    onComplete: handleStreamComplete,
  });

  // Auto-scroll to bottom when new messages arrive or streaming content changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [
    aiAssistantStore.messages.length,
    aiAssistantStore.isLoading,
    aiAssistantStore.isStreaming,
    aiAssistantStore.streamingContent,
  ]);

  const handleSuggestionClick = async (query: string) => {
    // Get valid auth token
    const token = await tokenManager.getValidAccessToken();
    if (!token) {
      aiAssistantStore.handleStreamingError('Sesión expirada. Por favor, iniciá sesión nuevamente.');
      return;
    }

    // Start streaming in the store
    aiAssistantStore.startStreaming(query);

    // Send the message via SSE
    await sendStreamMessage(query, token, aiAssistantStore.currentConversationId);
  };

  if (aiAssistantStore.messages.length === 0 && !aiAssistantStore.isBusy) {
    return <WelcomeMessage onSuggestionClick={handleSuggestionClick} />;
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        overflow: 'auto',
        p: 2,
        '@media print': {
          overflow: 'visible',
        },
      }}
    >
      {aiAssistantStore.messages.map((message, index) => (
        <Box
          key={`${message.timestamp}-${index}`}
          sx={{
            '@media print': {
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
            },
          }}
        >
          <AiAssistantMessage message={message} />
        </Box>
      ))}

      {/* Show streaming content as it arrives */}
      {aiAssistantStore.isStreaming && aiAssistantStore.streamingContent && (
        <Box
          sx={{
            '@media print': {
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
            },
          }}
        >
          <AiAssistantMessage
            message={{
              role: 'assistant',
              content: aiAssistantStore.streamingContent,
              timestamp: new Date().toISOString(),
            }}
            isStreaming
          />
        </Box>
      )}

      {/* Streaming indicator (tool status or "thinking") */}
      {aiAssistantStore.isStreaming && (
        <StreamingIndicator
          toolStatus={aiAssistantStore.streamingToolStatus}
          content={aiAssistantStore.streamingContent}
        />
      )}

      {/* Show query explanation and evaluation form after streaming completes */}
      {!aiAssistantStore.isStreaming && aiAssistantStore.messages.length > 0 && (
        <Box sx={{ px: 2, pb: 1 }}>
          {aiAssistantStore.lastExplanation && (
            <QueryExplanationPanel explanation={aiAssistantStore.lastExplanation} />
          )}
          {/* Evaluation form for the last response (only when enabled) */}
          {aiAssistantStore.evaluationEnabled && (() => {
            const lastAssistantMsg = [...aiAssistantStore.messages].reverse().find(m => m.role === 'assistant');
            if (lastAssistantMsg?.responseId) {
              return (
                <EvaluationForm
                  responseId={lastAssistantMsg.responseId}
                  onSave={async (data) => {
                    await aiAssistantStore.saveEvaluation(data);
                  }}
                />
              );
            }
            return null;
          })()}
        </Box>
      )}

      {/* Legacy loading indicator for non-streaming operations */}
      {aiAssistantStore.isLoading && !aiAssistantStore.isStreaming && <LoadingIndicator />}

      {aiAssistantStore.error && (
        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
            border: 1,
            borderColor: 'error.main',
          }}
        >
          <Typography variant="body2" color="error">
            {aiAssistantStore.error}
          </Typography>
        </Box>
      )}

      <div ref={messagesEndRef} />
    </Box>
  );
});

export default AiAssistantChat;
